import React, { createContext, useContext, useEffect, useState } from 'react';

import type { ReactNode } from 'react';

import type { CourseModel } from 'Courses/data';
import type { UserDisplayModel } from 'User/userDisplayModel';

import { exitBrowserFullscreen } from './fullscreen-utils';

import type { DesktopModel } from 'Api';

interface BasicFullscreenState {
  isFullscreen: boolean;
  isBrowserFullscreen: boolean;
  fullscreenElement: Element | null;
  currentDesktop: DesktopModel | null;
  currentCourse: CourseModel | null;
  currentUser: UserDisplayModel | null;
  isFreshStartDesktop: boolean;
  isParticipantDesktop: boolean;
  isTrainerDesktop: boolean;
}

interface BasicFullscreenActions {
  enterFullscreen: (
    element: HTMLElement,
    desktop: DesktopModel,
    course: CourseModel,
    user: UserDisplayModel,
    options?: {
      isFreshStartDesktop?: boolean;
      isParticipantDesktop?: boolean;
      isTrainerDesktop?: boolean;
    }
  ) => void;
  exitFullscreen: () => void;
}

type BasicFullscreenContextType = BasicFullscreenState & BasicFullscreenActions;

const BasicFullscreenContext = createContext<BasicFullscreenContextType | undefined>(undefined);

interface BasicFullscreenProviderProps {
  children: ReactNode;
}

export const BasicFullscreenProvider: React.FC<BasicFullscreenProviderProps> = ({ children }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isBrowserFullscreen, setIsBrowserFullscreen] = useState(false);
  const [fullscreenElement, setFullscreenElement] = useState<Element | null>(null);
  const [currentDesktop, setCurrentDesktop] = useState<DesktopModel | null>(null);
  const [currentCourse, setCurrentCourse] = useState<CourseModel | null>(null);
  const [currentUser, setCurrentUser] = useState<UserDisplayModel | null>(null);
  const [isFreshStartDesktop, setIsFreshStartDesktop] = useState(false);
  const [isParticipantDesktop, setIsParticipantDesktop] = useState(false);
  const [isTrainerDesktop, setIsTrainerDesktop] = useState(false);

  const enterFullscreen = (
    element: HTMLElement,
    desktop: DesktopModel,
    course: CourseModel,
    user: UserDisplayModel,
    options?: {
      isFreshStartDesktop?: boolean;
      isParticipantDesktop?: boolean;
      isTrainerDesktop?: boolean;
    }
  ) => {
    setCurrentDesktop(desktop);
    setCurrentCourse(course);
    setCurrentUser(user);
    setIsFreshStartDesktop(options?.isFreshStartDesktop ?? false);
    setIsParticipantDesktop(options?.isParticipantDesktop ?? false);
    setIsTrainerDesktop(options?.isTrainerDesktop ?? false);

    document.documentElement
      .requestFullscreen()
      .then(() => {
        setIsBrowserFullscreen(true);
        setIsFullscreen(true);
        setFullscreenElement(element);
      })
      .catch(err => {
        console.warn('Failed to enter browser fullscreen:', err);
        setCurrentDesktop(null);
        setCurrentCourse(null);
        setCurrentUser(null);
      });
  };

  const exitFullscreen = () => {
    exitBrowserFullscreen().finally(() => {
      setTimeout(() => {
        setIsFullscreen(false);
        setIsBrowserFullscreen(false);
        setFullscreenElement(null);
        setCurrentDesktop(null);
        setCurrentCourse(null);
        setCurrentUser(null);
        setIsFreshStartDesktop(false);
        setIsTrainerDesktop(false);
      }, 100);
    });
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && isFullscreen) {
        exitFullscreen();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [isFullscreen]);

  const contextValue: BasicFullscreenContextType = {
    isFullscreen,
    isBrowserFullscreen,
    fullscreenElement,
    currentDesktop,
    currentCourse,
    currentUser,
    isFreshStartDesktop,
    isParticipantDesktop,
    isTrainerDesktop,
    enterFullscreen,
    exitFullscreen
  };

  return (
    <BasicFullscreenContext.Provider value={contextValue}>
      {children}
    </BasicFullscreenContext.Provider>
  );
};

export const useBasicFullscreen = (): BasicFullscreenContextType => {
  const context = useContext(BasicFullscreenContext);

  if (context === undefined) {
    throw new Error('useBasicFullscreen must be used within a BasicFullscreenProvider');
  }

  return context;
};

export const useBasicFullscreenSafe = (): BasicFullscreenContextType | null => {
  const context = useContext(BasicFullscreenContext);
  return context ?? null;
};
